import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { LegalHomeButton } from "@/components/legal-home-button";

export const Route = createFileRoute("/agb")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Allgemeine Geschäftsbedingungen. Begner" },
      {
        name: "description",
        content:
          "Allgemeine Geschäftsbedingungen für die Nutzung der Begner-Plattform, Mitgliedschaften und Immobilieninserate.",
      },
    ],
  }),
  component: TermsPage,
});

const sections: Array<{
  id: string;
  title: string;
  content: ReactNode;
}> = [
  {
    id: "geltungsbereich",
    title: "Geltungsbereich und Vertragspartner",
    content: (
      <>
        <p>
          Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der Website begner.ch
          und der darüber angebotenen Dienste. Betreiberin und Vertragspartnerin ist SEO Begani,
          Weiermattstrasse 4, 4410 Liestal, Schweiz (nachfolgend «Begner»).
        </p>
        <p>
          Die AGB gelten für Besucherinnen und Besucher, Auftraggebende, registrierte Auftragnehmer,
          Immobilienmakler sowie Personen und Unternehmen, die ein Immobilieninserat
          veröffentlichen. Abweichende Individualvereinbarungen, Angebotsbeschreibungen und
          Rechnungen gehen diesen AGB im Widerspruchsfall vor.
        </p>
      </>
    ),
  },
  {
    id: "plattform",
    title: "Rolle und Leistungen von Begner",
    content: (
      <>
        <p>
          Begner stellt eine digitale Plattform bereit, auf der Aufträge veröffentlicht,
          Anbieterprofile dargestellt, geschützte Kontaktdaten freigeschaltet und Immobilieninserate
          publiziert werden können. Kostenrechner, Richtpreise und redaktionelle Inhalte dienen
          ausschliesslich der Orientierung.
        </p>
        <p>
          Begner vermittelt Kontakte und stellt die technische Infrastruktur bereit. Begner wird
          nicht Partei der Verträge, die Nutzerinnen und Nutzer untereinander schliessen, und ist
          insbesondere weder Werkunternehmerin noch Immobilienmaklerin, Verkäuferin, Vermieterin
          oder Vertreterin einer Partei. Abschluss, Inhalt und Abwicklung solcher Verträge liegen
          allein bei den jeweiligen Parteien.
        </p>
      </>
    ),
  },
  {
    id: "konto",
    title: "Registrierung und Nutzerkonto",
    content: (
      <>
        <p>
          Für einzelne Funktionen ist ein Nutzerkonto erforderlich. Die bei Registrierung und
          Nutzung gemachten Angaben müssen vollständig, aktuell und wahrheitsgemäss sein.
          Geschäftliche Konten dürfen nur von entsprechend bevollmächtigten Personen angelegt und
          genutzt werden.
        </p>
        <p>
          Zugangsdaten sind vertraulich zu behandeln. Nutzerinnen und Nutzer informieren Begner
          unverzüglich über eine vermutete unbefugte Nutzung ihres Kontos. Konten dürfen ohne
          Zustimmung von Begner nicht übertragen oder Dritten zur dauerhaften Nutzung überlassen
          werden.
        </p>
      </>
    ),
  },
  {
    id: "auftraege",
    title: "Aufträge und Kontaktvermittlung",
    content: (
      <>
        <p>
          Auftraggebende können Projektanfragen zur Prüfung einreichen. Soweit bei der Einreichung
          nichts anderes ausgewiesen ist, ist das Einstellen eines Auftrags kostenlos. Begner kann
          Angaben vor der Veröffentlichung prüfen, Rückfragen stellen, redaktionell vereinheitlichen
          oder eine Veröffentlichung aus sachlichen Gründen ablehnen.
        </p>
        <p>
          Öffentliche Auftragsangaben dürfen keine unnötigen Personendaten oder rechtswidrigen
          Inhalte enthalten. Nach Veröffentlichung können geprüfte Auftragnehmer mit aktivem
          Pro-Zugang während 72 Stunden Interesse bekunden. Anschliessend wählen Auftraggebende
          innerhalb der angezeigten Auswahlfrist bis zu fünf Firmen aus. Geschützte Kontaktdaten
          werden ausschliesslich den ausdrücklich ausgewählten Firmen angezeigt. Eine Mitgliedschaft
          allein berechtigt nicht zum Abruf dieser Kontaktdaten. Ein späteres Entfernen des Auftrags
          oder Beenden einer Freigabe kann bereits eingesehene Daten nicht zurückholen.
        </p>
        <p>
          Eine Interessebekundung oder Kontaktfreigabe ist keine Auftragsvergabe und begründet
          keinen Anspruch auf Auswahl, Kontaktaufnahme oder einen Vertragsabschluss. Nach Ablauf
          einer Auswahlfrist ohne Entscheidung bleiben die Kontaktdaten gesperrt. Die im Konto
          angezeigten Fristen und Verlängerungsmöglichkeiten gelten für den jeweiligen Auftrag.
        </p>
        <p>
          Auftragnehmer dürfen freigeschaltete Kontaktdaten ausschliesslich zur Bearbeitung des
          konkreten Auftrags verwenden. Unverlangte Massenwerbung, Weiterverkauf, Veröffentlichung
          oder Nutzung für andere Zwecke ist untersagt.
        </p>
      </>
    ),
  },
  {
    id: "anbieter",
    title: "Anbieterprofile und Prüfung",
    content: (
      <>
        <p>
          Auftragnehmer und Immobilienmakler sind für die Richtigkeit ihrer Firmen-, Leistungs-,
          Zulassungs- und Kontaktdaten verantwortlich. Sie müssen über alle Bewilligungen,
          Qualifikationen und Versicherungen verfügen, die für ihre Tätigkeit erforderlich sind.
        </p>
        <p>
          Hinweise wie «geprüft» bedeuten, dass Begner ausgewählte Angaben und verfügbare Nachweise
          plausibilisiert hat. Sie sind keine behördliche Zulassung, Zertifizierung oder Garantie
          für Fachkunde, Bonität, Verfügbarkeit, Qualität oder ein bestimmtes Arbeitsergebnis.
          Nutzerinnen und Nutzer bleiben dafür verantwortlich, Anbieter und Angebote vor einem
          Vertragsschluss selbst zu prüfen.
        </p>
      </>
    ),
  },
  {
    id: "immobilien",
    title: "Immobilieninserate",
    content: (
      <>
        <p>
          Inserierende sichern zu, dass sie zur Veröffentlichung des Angebots und aller verwendeten
          Inhalte berechtigt sind. Objektangaben, Preise, Nebenkosten, Verfügbarkeit, Bilder und
          sonstige Informationen müssen richtig, aktuell und dürfen nicht irreführend sein.
        </p>
        <p>
          Immobilieninserate werden vor der Veröffentlichung geprüft. Die gebuchte Laufzeit von 14
          oder 30 Tagen beginnt erst, wenn die Zahlung bestätigt und das Inserat freigeschaltet ist.
          Eine vorzeitige Entfernung auf Wunsch der inserierenden Person begründet grundsätzlich
          keinen anteiligen Rückerstattungsanspruch; zwingende gesetzliche Ansprüche und abweichende
          Vereinbarungen bleiben vorbehalten.
        </p>
        <p>
          Begner schuldet die Veröffentlichung für die gebuchte Laufzeit, nicht aber eine bestimmte
          Anzahl Aufrufe oder Anfragen und keinen erfolgreichen Verkauf oder Vertragsabschluss.
        </p>
      </>
    ),
  },
  {
    id: "vertragsschluss",
    title: "Vertragsschluss",
    content: (
      <>
        <p>
          Darstellungen von Preisen und Leistungen auf der Website sind Einladungen zur Abgabe einer
          Bestellung. Ein kostenpflichtiger Vertrag kommt zustande, wenn die Nutzerin oder der
          Nutzer das gewählte Angebot anfordert und Begner die Bestellung ausdrücklich bestätigt,
          die Rechnung ausstellt oder die Leistung freischaltet. Eine reine Eingangsbestätigung
          stellt noch keine Annahme dar, sofern darin nichts anderes erklärt wird.
        </p>
        <p>
          Vor dem Absenden sind die gewählte Leistung, der Preis und die eingegebenen Angaben zu
          prüfen. Offensichtliche Eingabefehler sind Begner unverzüglich mitzuteilen.
        </p>
      </>
    ),
  },
  {
    id: "preise",
    title: "Preise, Rechnungen und Zahlung",
    content: (
      <>
        <p>
          Es gelten die beim Abschluss angezeigten Preise in Schweizer Franken (CHF). Ob und in
          welcher Höhe Steuern enthalten sind, ergibt sich aus dem jeweiligen Angebot und der
          Rechnung. Kostenpflichtige Leistungen werden im Voraus per Schweizer Rechnung
          beziehungsweise Banküberweisung bezahlt; eine automatische Kreditkartenbelastung findet
          nicht statt.
        </p>
        <p>
          Rechnungen sind bis zum darauf angegebenen Fälligkeitsdatum ohne Abzug zu begleichen.
          Begner kann die Freischaltung bis zum bestätigten Zahlungseingang zurückhalten und den
          Zugang bei ausbleibender Zahlung nach Massgabe des Vertrags und der gesetzlichen
          Vorschriften einschränken. Gesetzliche Verzugsfolgen bleiben vorbehalten.
        </p>
      </>
    ),
  },
  {
    id: "mitgliedschaften",
    title: "Mitgliedschaften, Verlängerung und Kündigung",
    content: (
      <>
        <p>
          Kostenpflichtige Anbieter-Mitgliedschaften werden monatlich im Voraus abgerechnet. Sie
          verlängern sich jeweils um einen weiteren Monat, solange sie nicht zum Ende der laufenden
          bezahlten Periode gekündigt werden. Die Kündigung kann im Nutzerkonto vorgemerkt werden;
          bis zum bereits bezahlten Periodenende bleibt der Zugang aktiv.
        </p>
        <p>
          Für die Folgeperiode bereits erstellte, noch nicht geschuldete Rechnungen werden bei einer
          rechtzeitigen Kündigung storniert. Die Kündigung kann bis zum Periodenende im Nutzerkonto
          zurückgenommen werden. Separat gebuchte Immobilieninserate sind keine Mitgliedschaften und
          verlängern sich nicht automatisch.
        </p>
      </>
    ),
  },
  {
    id: "widerruf",
    title: "Widerruf und Rückerstattungen",
    content: (
      <p>
        Das schweizerische Recht sieht für online abgeschlossene Verträge kein allgemeines
        gesetzliches Widerrufsrecht vor. Begner gewährt kein zusätzliches pauschales Widerrufsrecht.
        Individuell zugesagte Rücktrittsrechte, Ansprüche wegen nicht vertragsgemässer Leistung und
        zwingende gesetzliche Rechte bleiben uneingeschränkt bestehen. Bewilligte Rückerstattungen
        werden über das mitgeteilte Verfahren abgewickelt und dokumentiert.
      </p>
    ),
  },
  {
    id: "inhalte",
    title: "Nutzerinhalte und Nutzungsrechte",
    content: (
      <>
        <p>
          Nutzerinnen und Nutzer behalten ihre Rechte an Texten, Bildern, Logos und weiteren
          Inhalten. Sie räumen Begner für die Dauer und den Zweck der Plattformnutzung ein
          nicht-exklusives, räumlich unbeschränktes Recht ein, diese Inhalte technisch zu speichern,
          zu bearbeiten, darzustellen und zu vervielfältigen, soweit dies für Prüfung,
          Veröffentlichung und Betrieb der gebuchten Leistung erforderlich ist.
        </p>
        <p>
          Unzulässig sind insbesondere rechtswidrige, täuschende, beleidigende, diskriminierende,
          sicherheitsgefährdende oder fremde Rechte verletzende Inhalte sowie Schadsoftware und
          manipulierte Bewertungen. Bewertungen müssen auf einer eigenen tatsächlichen Erfahrung
          beruhen und sachlich formuliert sein.
        </p>
      </>
    ),
  },
  {
    id: "moderation",
    title: "Moderation, Sperrung und Entfernung",
    content: (
      <p>
        Begner kann Inhalte zurückweisen, korrigieren, vorübergehend ausblenden oder entfernen und
        Konten oder einzelne Funktionen einschränken, wenn konkrete Anhaltspunkte für falsche
        Angaben, Rechtsverletzungen, Missbrauch, Sicherheitsrisiken, ausstehende Zahlungen oder
        Verstösse gegen diese AGB bestehen. Soweit zumutbar, wird die betroffene Person vorgängig
        informiert und erhält Gelegenheit zur Stellungnahme oder Behebung. Bei dringenden Risiken
        darf Begner sofort handeln.
      </p>
    ),
  },
  {
    id: "verfuegbarkeit",
    title: "Verfügbarkeit und Änderungen",
    content: (
      <p>
        Begner bemüht sich um einen sicheren und zuverlässigen Betrieb, garantiert jedoch keine
        ununterbrochene oder fehlerfreie Verfügbarkeit. Wartung, Sicherheitsmassnahmen und Umstände
        ausserhalb des Einflussbereichs von Begner können zu Unterbrüchen führen. Bei einer von
        Begner zu vertretenden erheblichen Unterbrechung einer bezahlten Veröffentlichung wird die
        Laufzeit angemessen verlängert oder eine gleichwertige Lösung angeboten.
      </p>
    ),
  },
  {
    id: "haftung",
    title: "Haftung",
    content: (
      <>
        <p>
          Begner haftet für eigene Vertragsverletzungen nach den gesetzlichen Vorschriften. Soweit
          gesetzlich zulässig, ist die Haftung für leichte Fahrlässigkeit sowie für indirekte
          Schäden, Folgeschäden, entgangenen Gewinn und Datenverlust ausgeschlossen. Zwingende
          gesetzliche Haftung, Personenschäden sowie Schäden aus Absicht oder grober Fahrlässigkeit
          bleiben davon unberührt.
        </p>
        <p>
          Begner haftet nicht für Handlungen, Angebote, Inhalte oder Leistungen anderer Nutzerinnen,
          Nutzer oder Dritter, soweit Begner kein eigenes Verschulden trifft. Insbesondere übernimmt
          Begner keine Gewähr für das Zustandekommen oder die ordnungsgemässe Erfüllung eines
          vermittelten Werk-, Dienstleistungs-, Kauf-, Miet- oder Maklervertrags.
        </p>
      </>
    ),
  },
  {
    id: "datenschutz",
    title: "Datenschutz",
    content: (
      <p>
        Die Bearbeitung personenbezogener Daten richtet sich nach der jeweils aktuellen
        <Link
          to="/datenschutz"
          className="mx-1 font-semibold text-brand-deep underline-offset-4 hover:underline"
        >
          Datenschutzerklärung
        </Link>
        von Begner. Für Inhalte und Daten, die Anbieter nach einer zulässigen Kontaktfreigabe in
        eigener Verantwortung bearbeiten, sind diese selbst datenschutzrechtlich verantwortlich.
      </p>
    ),
  },
  {
    id: "schlussbestimmungen",
    title: "Änderungen und Schlussbestimmungen",
    content: (
      <>
        <p>
          Begner kann diese AGB mit Wirkung für die Zukunft anpassen, insbesondere bei Änderungen
          der Leistungen oder der Rechtslage. Wesentliche Änderungen an laufenden
          Dauerschuldverhältnissen werden in geeigneter Form mitgeteilt. Zwingende Rechte und
          bereits vollständig bezahlte Leistungsperioden werden dadurch nicht rückwirkend
          eingeschränkt.
        </p>
        <p>
          Es gilt schweizerisches Recht. Für geschäftliche Nutzer ist, soweit gesetzlich zulässig,
          Liestal Gerichtsstand. Für Konsumentinnen und Konsumenten gelten die zwingenden
          gesetzlichen Gerichtsstände. Sollte eine Bestimmung unwirksam sein, bleiben die übrigen
          Bestimmungen davon unberührt.
        </p>
      </>
    ),
  },
];

function TermsPage() {
  return (
    <main>
      <section className="border-b border-border/70 bg-surface">
        <div className="container-page py-14 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-deep">
            Rechtliches
          </p>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Allgemeine Geschäftsbedingungen
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
            Bedingungen für die Nutzung der Begner-Plattform, Anbieter-Mitgliedschaften und
            Immobilieninserate.
          </p>
          <p className="mt-5 text-sm font-medium text-foreground">Stand: 4. Oktober 2026</p>
          <div className="mt-7">
            <LegalHomeButton />
          </div>
        </div>
      </section>

      <section className="container-page py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
          <aside className="h-fit rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8 lg:sticky lg:top-24">
            <h2 className="font-display text-xl font-bold">Inhalt</h2>
            <nav aria-label="Inhaltsverzeichnis der AGB">
              <ol className="mt-5 space-y-3 text-sm">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex gap-2 text-muted-foreground transition-colors hover:text-brand-deep"
                    >
                      <span className="w-6 shrink-0 font-semibold text-foreground">
                        {index + 1}.
                      </span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="mt-7 border-t border-border pt-6 text-sm leading-6 text-muted-foreground">
              <p>Fragen zu diesen AGB?</p>
              <a
                href="mailto:info@begner.ch"
                className="font-semibold text-brand-deep underline-offset-4 hover:underline"
              >
                info@begner.ch
              </a>
            </div>
          </aside>

          <div className="divide-y divide-border">
            {sections.map((section, index) => (
              <article
                key={section.id}
                id={section.id}
                className={`scroll-mt-24 ${index === 0 ? "pb-10" : "py-10"}`}
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
                  {index + 1}
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  {section.title}
                </h2>
                <div className="mt-4 max-w-3xl space-y-4 text-base leading-8 text-muted-foreground">
                  {section.content}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
